import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { EventsDataService, EventsColumnsService } from '../../services';
import { DialogEventFormComponent } from './event-form/event-form.component';
import { DialogEventDetailsComponent } from './event-details/event-details.component';
import { Event, EventsResult } from '../../interfaces';

@Component({
  selector: 'app-posts',
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class EventsComponent implements OnInit, OnDestroy {

  private eventAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private eventEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private eventDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public eventsToDelete: Event = {};
  public events: Event[] = [];
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  private _selectedColumns: Column[] = [];

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private eventsDataService: EventsDataService,
    private eventsColumnsService: EventsColumnsService,
  ) {
  }

  ngOnInit(): void {
    this.isInitialized = true;
    this.titleService.setTitle('Events - Management');
    this.primengConfig.ripple = true;
    this.getEvents();
  }

  ngOnDestroy(): void {
    this.isInitialized = false;
  }

  getEvents(): void {
    this.isLoading = true;
    if (!this.isInitialized) {
      return;
    }
    this.eventsDataService.getEvents().subscribe((response: EventsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        this.events = response.success.data;
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('UI.ERROR'), detail: this.translate.instant('UI.eventSNOTLOADED')});
    });
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.eventsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('posts.' + this.columns[i].field + '.showFiled', show);
    }
  }

  exportPdf() {
    ToolsService.exportPdf(this.events, this.columns, 'Events');
  }

  exportExcel() {
    ToolsService.exportExcel(this.events, this.columns, 'Events');
  }

  exportCsv() {
    ToolsService.exportCsv(this.events, this.columns, 'Events');
  }

  refresh() {
    this.getEvents();
  }

  eventAdd(): void {
    if (!this.navigation.getParamValue('posts.addInDialog', true)) {
      this.router.navigate(['/events/add']);
      return;
    }
    this.eventAddDialog = this.dialogService.open(DialogEventFormComponent, {
      header: '', // this.translate.instant('NEWevent'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        post: {id: null},
      },
    });

    this.eventAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Event added'});
        this.getEvents();
      }
    });
  }

  eventEdit(event: Event): void {
    if (!this.navigation.getParamValue('posts.editInDialog', true)) {
      this.router.navigate(['/events/edit/' + event.id]);
      return;
    }
    this.eventEditDialog = this.dialogService.open(DialogEventFormComponent, {
      header: '', // this.translate.instant('EDITevent') + ' ' + event.title,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        post: event,
       // users: this.postsColumnsService.users,
      },
    });

    this.eventEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'event updated'});
        this.getEvents();
      }
    });
  }

  eventDetails(event: Event): void {
    if (!this.navigation.getParamValue('posts.detailsInDialog', true)) {
      this.router.navigate(['/events/details/' + event.id]);
      return;
    }
    this.eventDetailsDialog = this.dialogService.open(DialogEventDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        post: event,
      }
    });
  }

  eventDelete(event: Event): void {
    this.eventsToDelete = event;
    this.messageService.clear();
    this.messageService.add({
      key: 'postDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + event.title + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.eventsDataService.deleteEvent(this.eventsToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Event deleted'});
      this.getEvents();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Event not deleted'});
    });
  }

  onDeleteReject(): void {
    this.eventsToDelete = {};
    this.messageService.clear('postDelete');
  }

}
